import { useContext, useRef, useState } from 'react'
import Modal from 'react-modal'
import {
  FromB1fEnemyRank,
  FromBoss,
  FromFloorCount,
  FromMapLevel,
  FromMapName1,
  FromMapName2,
  FromMapName3,
  FromMapRank,
  FromMapSeed,
  FromMapType,
  FromOnlyEnemy,
  FromPlace,
  FromSpecialFloor,
  FromTreasureBox
} from '@/views/modal/SearchTargets'
import { SearchConditionContext } from '@/App'

type Props = {
  isOpen: boolean
  closeModal: () => void
  editUuid: string
}

const EditSearchConditionModal = ({ isOpen, closeModal, editUuid }: Props) => {
  // #region フック

  const [, dispatch] = useState({})

  const searchCondition = useContext(SearchConditionContext)

  const searchTarget = useRef(0)

  // #endregion フック

  const editCondition = searchCondition.conditionList.find((s) => s.uuid === editUuid)

  // #region スタイル

  const modalStyle: Modal.Styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      backgroundColor: 'rgba(240,240,240,0.7)'
    },
    content: {
      position: 'absolute',
      backgroundColor: '#f0f0f0',
      border: 'solid 1px #000000',
      top: '20%',
      left: '35%',
      right: '35%',
      bottom: '50%',
      padding: '0px'
    }
  }

  const headerStyle: React.CSSProperties = {
    margin: '5px 0px 0px 10px'
  }

  const mainStyle: React.CSSProperties = {
    margin: '10px 10px 10px 50px'
  }

  const pulldownStyle: React.CSSProperties = {
    fontSize: '15px',
    height: '24px'
  }

  const targetFormsStyle: React.CSSProperties = {
    height: '120px',
    display: 'flex'
  }

  const actionButtonStyle: React.CSSProperties = {
    textAlign: 'right'
  }

  const applyButtonStyle: React.CSSProperties = {
    marginRight: '10px'
  }

  // #endregion スタイル

  // #region イベント

  const onAfterOpenModal = () => {
    if (editUuid !== '') {
      if (!editCondition?.key) {
        return
      }
      switch (editCondition.key) {
        case 'mapRank[]':
          searchTarget.current = 0
          break
        case 'mapSeed[]':
          searchTarget.current = 1
          break
        case 'mapName1Index[]':
          searchTarget.current = 2
          break
        case 'mapName2Index[]':
          searchTarget.current = 3
          break
        case 'mapName3Index[]':
          searchTarget.current = 4
          break
        case 'mapLevel[]':
          searchTarget.current = 5
          break
        case 'places[]':
          searchTarget.current = 6
          break
        case 'mapTypeIndex[]':
          searchTarget.current = 7
          break
        case 'floorCount[]':
          searchTarget.current = 8
          break
        case 'b1fEnemyRank[]':
          searchTarget.current = 9
          break
        case 'bossIndex[]':
          searchTarget.current = 10
          break
        case 'boxS[]':
        case 'boxA[]':
        case 'boxB[]':
        case 'boxC[]':
        case 'boxD[]':
        case 'boxE[]':
        case 'boxF[]':
        case 'boxG[]':
        case 'boxH[]':
        case 'boxI[]':
        case 'boxTotalCount[]':
          searchTarget.current = 11
          break
        case 'isNoEnemy[]':
        case 'isOnly[]':
        case 'isFewEnemy[]':
          searchTarget.current = 12
          break
        case 'onlyEnemyId[]':
          searchTarget.current = 13
          break
      }
      dispatch({})
    }
  }

  const onChangeTarget = (e: React.ChangeEvent<HTMLSelectElement>) => {
    searchTarget.current = parseInt(e.target.value, 10)
    dispatch({})
  }

  const onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const key = (form.get('key') as string) || ''
    const value1 = (form.get('value1') as string) || ''
    const value2 = (form.get('value2') as string) || ''
    if (editUuid === '') {
      if (searchTarget.current === 6) {
        searchCondition.add(key, parseInt(value1, 16).toString(10))
      } else {
        searchCondition.add(key, `${value1.toUpperCase()}_${value2}`)
      }
    } else {
      if (searchTarget.current === 6) {
        searchCondition.applyChange(editUuid, key, parseInt(value1, 16).toString(10))
      } else {
        searchCondition.applyChange(editUuid, key, `${value1.toUpperCase()}_${value2}`)
      }
    }
    searchTarget.current = 0
    closeModal()
  }

  const onClickCancel = () => {
    searchTarget.current = 0
    closeModal()
  }

  // #endregion イベント

  const targetList = [
    'Rank',
    'Seed',
    '地図名1',
    '地図名2',
    '地図名3',
    '地図Lv',
    '場所候補',
    '地形',
    '深さ',
    'B1F敵Rank',
    'ボス',
    '宝箱の数',
    '特殊フロア',
    'オンリーモンスター'
  ]

  const treasureBoxKeys = [
    'boxS[]',
    'boxA[]',
    'boxB[]',
    'boxC[]',
    'boxD[]',
    'boxE[]',
    'boxF[]',
    'boxG[]',
    'boxH[]',
    'boxI[]',
    'boxTotalCount[]'
  ]

  const specialFloorKeys = ['isNoEnemy[]', 'isOnly[]', 'isFewEnemy[]']

  return (
    <Modal isOpen={isOpen} style={modalStyle} ariaHideApp={false} onAfterOpen={onAfterOpenModal}>
      {editUuid === '' ? (
        <h1 style={headerStyle}>検索条件追加</h1>
      ) : (
        <h1 style={headerStyle}>検索条件編集</h1>
      )}
      <main style={mainStyle}>
        対象：
        <select style={pulldownStyle} value={searchTarget.current} onChange={onChangeTarget}>
          {targetList.map((s, i) => (
            <option key={`${i}_${s}`} value={i}>
              {s}
            </option>
          ))}
        </select>
        <form onSubmit={onSubmitForm}>
          <div style={targetFormsStyle}>
            {searchTarget.current === 0 && (
              <FromMapRank
                value1={editCondition?.key === 'mapRank[]' ? editCondition.value.split('_')[0] : '02'}
                value2={editCondition?.key === 'mapRank[]' ? editCondition.value.split('_')[1] : '0'}
              />
            )}
            {searchTarget.current === 1 && (
              <FromMapSeed
                value1={editCondition?.key === 'mapSeed[]' ? editCondition.value.split('_')[0] : ''}
                value2={editCondition?.key === 'mapSeed[]' ? editCondition.value.split('_')[1] : '0'}
              />
            )}
            {searchTarget.current === 2 && (
              <FromMapName1
                value1={editCondition?.key === 'mapName1Index[]' ? editCondition.value.split('_')[0] : '0'}
                value2={editCondition?.key === 'mapName1Index[]' ? editCondition.value.split('_')[1] : '0'}
              />
            )}
            {searchTarget.current === 3 && (
              <FromMapName2
                value1={editCondition?.key === 'mapName2Index[]' ? editCondition.value.split('_')[0] : '0'}
                value2={editCondition?.key === 'mapName2Index[]' ? editCondition.value.split('_')[1] : '0'}
              />
            )}
            {searchTarget.current === 4 && (
              <FromMapName3
                value1={editCondition?.key === 'mapName3Index[]' ? editCondition.value.split('_')[0] : '0'}
                value2={editCondition?.key === 'mapName3Index[]' ? editCondition.value.split('_')[1] : '0'}
              />
            )}
            {searchTarget.current === 5 && (
              <FromMapLevel
                value1={editCondition?.key === 'mapLevel[]' ? editCondition.value.split('_')[0] : ''}
                value2={editCondition?.key === 'mapLevel[]' ? editCondition.value.split('_')[1] : '0'}
              />
            )}
            {searchTarget.current === 6 && (
              <FromPlace
                value1={
                  editCondition?.key === 'places[]'
                    ? parseInt(editCondition.value, 10).toString(16).padStart(2, '0').toUpperCase()
                    : ''
                }
              />
            )}
            {searchTarget.current === 7 && (
              <FromMapType
                value1={editCondition?.key === 'mapTypeIndex[]' ? editCondition.value.split('_')[0] : '0'}
                value2={editCondition?.key === 'mapTypeIndex[]' ? editCondition.value.split('_')[1] : '0'}
              />
            )}
            {searchTarget.current === 8 && (
              <FromFloorCount
                value1={editCondition?.key === 'floorCount[]' ? editCondition.value.split('_')[0] : ''}
                value2={editCondition?.key === 'floorCount[]' ? editCondition.value.split('_')[1] : '0'}
              />
            )}
            {searchTarget.current === 9 && (
              <FromB1fEnemyRank
                value1={editCondition?.key === 'b1fEnemyRank[]' ? editCondition.value.split('_')[0] : ''}
                value2={editCondition?.key === 'b1fEnemyRank[]' ? editCondition.value.split('_')[1] : '0'}
              />
            )}
            {searchTarget.current === 10 && (
              <FromBoss
                value1={editCondition?.key === 'bossIndex[]' ? editCondition.value.split('_')[0] : '0'}
                value2={editCondition?.key === 'bossIndex[]' ? editCondition.value.split('_')[1] : '0'}
              />
            )}
            {searchTarget.current === 11 && (
              <FromTreasureBox
                queryKey={
                  treasureBoxKeys.includes(editCondition?.key || '')
                    ? editCondition?.key || treasureBoxKeys[0]
                    : treasureBoxKeys[0]
                }
                value1={
                  treasureBoxKeys.includes(editCondition?.key || '')
                    ? editCondition?.value.split('_')[0] || '0'
                    : '0'
                }
                value2={
                  treasureBoxKeys.includes(editCondition?.key || '')
                    ? editCondition?.value.split('_')[1] || '0'
                    : '0'
                }
              />
            )}
            {searchTarget.current === 12 && (
              <FromSpecialFloor
                queryKey={
                  specialFloorKeys.includes(editCondition?.key || '')
                    ? editCondition?.key || specialFloorKeys[0]
                    : specialFloorKeys[0]
                }
                value1={
                  specialFloorKeys.includes(editCondition?.key || '')
                    ? editCondition?.value.split('_')[0] || '0'
                    : '0'
                }
              />
            )}
            {searchTarget.current === 13 && (
              <FromOnlyEnemy
                value1={editCondition?.key === 'onlyEnemyId[]' ? editCondition.value.split('_')[0] : '0'}
                value2={editCondition?.key === 'onlyEnemyId[]' ? editCondition.value.split('_')[1] : '0'}
              />
            )}
          </div>
          <br />
          <div style={actionButtonStyle}>
            <input type='submit' style={applyButtonStyle} value={'適用'} />
            <button type='button' onClick={onClickCancel}>
              キャンセル
            </button>
          </div>
        </form>
      </main>
    </Modal>
  )
}

export { EditSearchConditionModal }
